import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import PageMetaLoader from '../components/PageMetaLoader';
import { en, km } from './AboutUsPage.data';
import { useI18n } from '../i18n';

/** Renders story copy, turning **text** into <strong>. */
function renderStoryParagraph(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

const AboutUsPage = () => {
  const { locale, localePath } = useI18n();
  const t = locale === 'km' ? km : en;
  const teamMembers = t.team.members;
  const values = t.values;

  return (
    <AboutContainer>
      <PageMetaLoader pageUrl="about-us" />
      <HeroSection>
        <HeroContent>
          <h1>{t.hero.title}</h1>
          <p>{t.hero.description}</p>
        </HeroContent>
      </HeroSection>

      <MissionSection>
        <h2>{t.mission.title}</h2>
        <p>{t.mission.description}</p>
      </MissionSection>

      <StorySection>
        <SectionTitle>{t.story.title}</SectionTitle>
        <StoryContent>
          {t.story.paragraphs.map((paragraph, index) => (
            <p key={index}>{renderStoryParagraph(paragraph)}</p>
          ))}
        </StoryContent>
      </StorySection>

      <ValuesSection>
        <SectionTitle>{t.valuesTitle}</SectionTitle>
        <ValuesGrid>
          {values.map((value, index) => (
            <ValueCard key={index}>
              <h3>{value.title}</h3>
              <ValueDescription>{value.description}</ValueDescription>
            </ValueCard>
          ))}
        </ValuesGrid>
      </ValuesSection>

      {/* <TeamSection>
        <SectionTitle>Our Team</SectionTitle>
        <p className="team-intro">
          Led by experienced software developers and business consultants, our team combines technical expertise with a practical understanding of how businesses actually operate.
        </p>
        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamMemberCard key={index}>
              <TeamMemberAvatar>
                {member.name.split(' ')[0][0]}{member.name.split(' ')[1][0]}
              </TeamMemberAvatar>
              <TeamMemberInfo>
                <h3>{member.name}</h3>
                <h4>{member.position}</h4>
                <p>{member.bio}</p>
              </TeamMemberInfo>
            </TeamMemberCard>
          ))}
        </TeamGrid>
      </TeamSection> */}

      <CTASection>
        <h2>{t.cta.title}</h2>
        <p>{t.cta.description}</p>
        <CTAButton to={localePath('/contact')}>{t.cta.button}</CTAButton>
      </CTASection>
    </AboutContainer>
  );
};

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
`;

const HeroSection = styled.section`
  position: relative;
  padding: 4rem 2rem;
  margin-bottom: 3rem;
  border-radius: 10px;
  background-color: var(--primary-light);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/images/about/about-hero.jpeg');
    background-size: cover;
    background-position: center;
    opacity: 0.15;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  
  h1 {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    color: var(--primary-color);
  }
  
  p {
    font-size: 1.25rem;
    line-height: 1.6;
    color: var(--text-dark);
  }
`;

const MissionSection = styled.section`
  background-color: var(--primary-color);
  color: white;
  padding: 3rem 2rem;
  text-align: center;
  border-radius: 10px;
  margin-bottom: 4rem;
  
  h2 {
    font-size: 2.2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.6;
    max-width: 800px;
    margin: 0 auto;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: var(--primary-color);
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background-color: var(--accent-color);
    margin: 1rem auto 0;
    border-radius: 2px;
  }
`;

const StorySection = styled.section`
  margin-bottom: 4rem;
`;

const StoryContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    color: var(--text-color);
  }
`;

const ValuesSection = styled.section`
  margin-bottom: 4rem;
`;

const ValueDescription = styled.div`
  color: var(--text-color);
  margin-bottom: 1.5rem;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const ValueCard = styled.div`
  background-color: var(--primary-light);
  padding: 2rem;
  border-radius: 8px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: var(--primary-color);
  }
  
  p {
    color: var(--text-dark);
    line-height: 1.6;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const TeamSection = styled.section`
  margin-bottom: 4rem;
  
  .team-intro {
    text-align: center;
    max-width: 800px;
    margin: 0 auto 3rem;
    font-size: 1.1rem;
    line-height: 1.6;
    color: var(--text-dark);
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  justify-items: center;
  max-width: 600px;
  margin: 0 auto;
`;

const TeamMemberCard = styled.div`
  display: flex;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const TeamMemberAvatar = styled.div`
  width: 80px;
  height: 80px;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const TeamMemberInfo = styled.div`
  padding: 1.5rem;
  
  h3 {
    font-size: 1.3rem;
    margin: 0 0 0.5rem;
    color: var(--primary-color);
  }
  
  h4 {
    font-size: 1rem;
    margin: 0 0 1rem;
    color: var(--accent-color);
    font-weight: 500;
  }
  
  p {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.6;
    color: var(--text-dark);
  }
`;

const CTASection = styled.div`
  background-color: var(--primary-color);
  color: white;
  padding: 3rem;
  text-align: center;
  border-radius: 10px;
  
  h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 2rem;
  background-color: white;
  color: var(--primary-color);
  font-weight: 500;
  border-radius: 40px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

export default AboutUsPage; 